/**
 * TakeLogStream — centralized copy strings (AOF 25, i18n posture).
 */

export const takeLogStreamCopy = {
  emptyLabel: "no log lines yet",

  /**
   * Why a terminal note carried no text (#153), for the log's reader.
   *
   * This surface has a different reader from the visitor conversation
   * and therefore a different sentence to write. A visitor gets a stage
   * direction and no mention of money, because they hold no invoice and
   * can do nothing with the fact. A developer reading a frame log is
   * exactly the person for whom "the audio leg ran and was billed, and
   * no model was asked" is the useful thing to know, so it is written
   * here in full.
   *
   * Without these a marked blank rendered a blank row: glyph, time,
   * speaker, and then nothing, which reads as a fault in the log rather
   * than as a fact about the turn.
   */
  emptyReason: {
    not_asked: "no model ran; the note was blank",
    not_heard: "nothing recognized; the audio leg was billed",
    no_reply: "asked, and nothing came back; billed in full",
  },

  scrollToggleOn: "follow the take: on",
  scrollToggleOff: "follow the take: off",
  ariaLabel: "take log stream",
  kindAgent: "voice",
  kindSystem: "system",
  kindCue: "cue",

  /**
   * The timeline ruler beneath the notes (DESIGN_SPEC §4.3).
   *
   * `rulerLabel` names the ruler for a screen reader, which otherwise
   * meets a row of bare numbers with no idea they are a time axis.
   *
   * `scrollHint` is the only instruction on this surface, and it exists
   * because the take log is the one control here whose axis is not the
   * one a keyboard user expects. A vertical log needs no hint: page-down
   * already works. A horizontal one moves under Left and Right, and a
   * hint that says so costs a line and saves the discovery.
   */
  rulerLabel: "timeline",
  scrollHint: "left and right arrows move through the take",

  /**
   * The head of the strip, where the next note will land.
   *
   * "now" rather than "live": live is the connection's word and it is
   * already spoken by the status lamp in the bar below. Two elements
   * saying "live" about different things is how a status line stops
   * being read.
   */
  nowLabel: "now",

  /**
   * The ruler's origin, drawn when there is nothing else on it.
   *
   * An empty take log still draws its ruler. A bare box saying "no log
   * lines yet" is a surface that has not started; a ruler with an origin
   * mark is a surface that is ready, which is what an empty rehearsal
   * stage actually is (`DESIGN_SPEC.md` §9: no skeletons, every empty
   * surface is a working one).
   */
  originLabel: "t=0.0s",
} as const;
